/*
 * Category-page redesign
 * Scoped to /collections/coffee-beans, /collections/filter-coffee,
 *           /collections/coffee-pods, /collections/instant-coffee
 *
 * Shipped to every visitor on 2026-10-30 after winning the 'cat-page-redesign'
 * PostHog experiment. Loaded from snippets/collection-redesign-head.liquid.
 * Drives the redesign's behaviour: quantity steppers, add to cart, the sticky
 * delivery bar, the pods machine chips, the mobile filter sheet and the beans
 * quick view, and reports their use to PostHog.
 *
 * facets.js replaces the innerHTML of #ProductGridContainer on every filter or
 * sort change, so all card handlers are delegated from `document` and nothing
 * here holds a reference to a card.
 */
(function () {
  'use strict';

  if (!window.CCX) return;

  var CCX = window.CCX;

  /* ---------------------------------------------------------------------
   * PostHog
   * ------------------------------------------------------------------- */

  function capture(event, props) {
    if (!window.posthog || typeof window.posthog.capture !== 'function') return;
    var payload = {
      collection: CCX.collection,
      collection_title: CCX.collectionTitle
    };
    for (var key in props) {
      if (Object.prototype.hasOwnProperty.call(props, key)) payload[key] = props[key];
    }
    window.posthog.capture(event, payload);
  }

  /* ---------------------------------------------------------------------
   * Money
   * ------------------------------------------------------------------- */

  var moneyFormat = '£{{amount}}';

  function readMoneyFormat() {
    var bar = document.querySelector('[data-ccx-delivery-bar]');
    if (bar && bar.dataset.moneyFormat) moneyFormat = bar.dataset.moneyFormat;
  }

  function formatMoney(pence) {
    var amount = Math.max(0, pence) / 100;

    function withCommas(value, decimals, decimalSep, thousandSep) {
      var fixed = value.toFixed(decimals);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
      return parts.join(decimalSep);
    }

    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, token) {
      switch (token) {
        case 'amount':
          return withCommas(amount, 2, '.', ',');
        case 'amount_no_decimals':
          return withCommas(amount, 0, '.', ',');
        case 'amount_with_comma_separator':
          return withCommas(amount, 2, ',', '.');
        case 'amount_no_decimals_with_comma_separator':
          return withCommas(amount, 0, ',', '.');
        case 'amount_with_space_separator':
          return withCommas(amount, 2, ',', ' ');
        case 'amount_no_decimals_with_space_separator':
          return withCommas(amount, 0, '.', ' ');
        default:
          return withCommas(amount, 2, '.', ',');
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Sticky free-delivery progress bar
   * ------------------------------------------------------------------- */

  var wasUnlocked = null;

  function renderDeliveryBar(cart) {
    var bar = document.querySelector('[data-ccx-delivery-bar]');
    if (!bar) return;

    var goal = parseInt(bar.dataset.goal, 10) || 6000;
    var total = typeof cart.total_price === 'number' ? cart.total_price : parseInt(bar.dataset.total, 10) || 0;
    var count = typeof cart.item_count === 'number' ? cart.item_count : parseInt(bar.dataset.count, 10) || 0;

    var pct = Math.min(100, (total / goal) * 100);
    var unlocked = total >= goal && count > 0;
    var remaining = Math.max(0, goal - total);

    bar.dataset.total = total;
    bar.dataset.count = count;
    bar.dataset.unlocked = unlocked ? 'true' : 'false';
    bar.style.setProperty('--ccx-pct', pct + '%');
    bar.style.setProperty('--ccx-min-width', count > 0 && !unlocked ? '10px' : '0px');

    var track = bar.querySelector('[data-ccx-delivery-track]');
    if (track) track.setAttribute('aria-valuenow', Math.round(pct));

    var message = bar.querySelector('[data-ccx-delivery-message]');
    if (message) {
      if (count === 0) {
        message.textContent = 'Free UK delivery on orders over £60';
      } else if (unlocked) {
        message.textContent = "You've unlocked FREE UK delivery ✓";
      } else {
        message.textContent = "You're only " + formatMoney(remaining) + ' away from FREE UK delivery';
      }
    }

    var countEl = bar.querySelector('[data-ccx-delivery-count]');
    if (countEl) countEl.textContent = count;

    var subtotalEl = bar.querySelector('[data-ccx-delivery-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(total);

    if (wasUnlocked === false && unlocked) {
      capture('delivery_bar_unlocked', { cart_total: total, cart_item_count: count });
    }
    wasUnlocked = unlocked;
  }

  function syncDeliveryBarFromCart() {
    return fetch(window.routes.cart_url + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        return response.json();
      })
      .then(renderDeliveryBar)
      .catch(function () {
        /* leave the server-rendered state in place */
      });
  }

  /* ---------------------------------------------------------------------
   * Add to cart
   * ------------------------------------------------------------------- */

  var ADDED_MS = 1600;

  function getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function addToCart(variantId, quantity) {
    var cartElement = getCartElement();
    var body = { items: [{ id: Number(variantId), quantity: Number(quantity) || 1 }] };

    if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
      body.sections = cartElement.getSectionsToRender().map(function (section) {
        return section.id;
      });
      body.sections_url = window.location.pathname;
      if (typeof cartElement.setActiveElement === 'function') {
        cartElement.setActiveElement(document.activeElement);
      }
    }

    var config = fetchConfig('javascript');
    config.body = JSON.stringify(body);

    return fetch(window.routes.cart_add_url, config)
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response.status) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'ccx-collection-redesign',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message
            });
          }
          throw new Error(response.description || response.message || 'Add to cart failed');
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'ccx-collection-redesign',
            productVariantId: variantId,
            cartData: response
          });
        }

        // The item is in the cart by this point. Rendering the theme's cart
        // notification is a separate concern: if it fails (a missing section in
        // the response, a third-party app), that must not turn a successful add
        // into an error path and rob the shopper of the "Added" confirmation.
        if (cartElement && typeof cartElement.renderContents === 'function') {
          try {
            if (cartElement.classList.contains('is-empty')) cartElement.classList.remove('is-empty');
            cartElement.renderContents(response);
          } catch (error) {
            console.error('[ccx] cart notification failed to render', error);
          }
        }

        return response;
      });
  }

  function flashAdded(button, addedLabel) {
    var original = button.dataset.ccxLabelDefault || button.textContent.trim();
    button.dataset.ccxLabelDefault = original;
    button.textContent = addedLabel || button.dataset.ccxLabelAdded || 'Added ✓';
    button.disabled = true;

    window.clearTimeout(button._ccxTimer);
    button._ccxTimer = window.setTimeout(function () {
      button.textContent = original;
      button.disabled = false;
    }, ADDED_MS);
  }

  /* ---------------------------------------------------------------------
   * Quantity steppers  (cards and quick view)
   * ------------------------------------------------------------------- */

  function stepQuantity(stepper, delta) {
    var input = stepper.querySelector('[data-ccx-qty]');
    if (!input) return;
    var next = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---------------------------------------------------------------------
   * Facets: chips and toggles drive the real checkboxes
   * ------------------------------------------------------------------- */

  function facetForm() {
    return document.getElementById('FacetFiltersForm');
  }

  function checkboxesFor(value) {
    var form = facetForm();
    if (!form) return [];
    return Array.prototype.filter.call(form.querySelectorAll('input[type="checkbox"]'), function (input) {
      return input.value === value;
    });
  }

  // facets.js listens for a bubbling `input` event on the form and debounces it
  // by 800ms, so dispatching from the checkbox is exactly what a real click on
  // the sidebar does: same fetch, same URL params, same server-rendered result.
  function submitFacets(input) {
    if (!input) return;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // Makes `value` the only active value in its group. Mirrors a sidebar click.
  function selectSingleFacetValue(value) {
    var matches = checkboxesFor(value);
    if (!matches.length) return false;

    var reference = matches[0];
    var group = reference.closest('.js-filter');
    if (!group) return false;

    Array.prototype.forEach.call(group.querySelectorAll('input[type="checkbox"]'), function (input) {
      input.checked = input.value === value;
    });

    submitFacets(reference);
    return true;
  }

  // Clears every value in the group that `sampleValue` belongs to.
  function clearFacetGroupOf(sampleValue) {
    var matches = checkboxesFor(sampleValue);
    if (!matches.length) return false;

    var group = matches[0].closest('.js-filter');
    if (!group) return false;

    Array.prototype.forEach.call(group.querySelectorAll('input[type="checkbox"]'), function (input) {
      input.checked = false;
    });

    submitFacets(matches[0]);
    return true;
  }

  function toggleFacetValue(value) {
    var matches = checkboxesFor(value);
    if (!matches.length) return false;
    var input = matches[0];
    input.checked = !input.checked;
    submitFacets(input);
    return input.checked;
  }

  /* ---------------------------------------------------------------------
   * Machine compatibility, client-side mode (pods)
   *
   * Used only while Search & Discovery has no compatibility facet. The grid is
   * filtered on the classification card-product.liquid already puts on each
   * card, so the chips, the sidebar group and the pills can never disagree.
   * Once the facet exists the Liquid stops rendering this mode and the chips
   * drive the real checkboxes instead.
   * ------------------------------------------------------------------- */

  var MACHINE_PARAM = 'machine';

  function machineRow() {
    return document.querySelector('[data-ccx-machine-row]');
  }

  function isClientMachineMode() {
    var row = machineRow();
    return !!row && row.dataset.ccxMachineMode === 'client';
  }

  function currentMachine() {
    try {
      return new URLSearchParams(window.location.search).get(MACHINE_PARAM) || '';
    } catch (error) {
      return '';
    }
  }

  function writeMachineParam(value) {
    try {
      var url = new URL(window.location.href);
      if (value) {
        url.searchParams.set(MACHINE_PARAM, value);
      } else {
        url.searchParams.delete(MACHINE_PARAM);
      }
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
      /* leave the URL alone */
    }
  }

  // Applies the current selection to the rendered grid. Safe to call repeatedly:
  // facets.js replaces #ProductGridContainer on every server-side filter or sort
  // change, and the MutationObserver in init() calls this again afterwards.
  function applyMachineFilter(value) {
    if (!isClientMachineMode()) return;

    var cards = document.querySelectorAll('#product-grid [data-ccx-card]');
    var counts = {};
    var shown = 0;

    Array.prototype.forEach.call(cards, function (card) {
      var compat = card.dataset.ccxCompat || '';
      counts[compat] = (counts[compat] || 0) + 1;

      var item = card.closest('.grid__item') || card;
      var matches = !value || compat === value;
      item.hidden = !matches;
      if (matches) shown++;
    });

    Array.prototype.forEach.call(document.querySelectorAll('[data-ccx-machine-count]'), function (el) {
      el.textContent = counts[el.dataset.ccxMachineCount] || 0;
    });

    Array.prototype.forEach.call(document.querySelectorAll('[data-ccx-machine-chip]'), function (chip) {
      chip.setAttribute('aria-pressed', (chip.dataset.ccxMachineValue || '') === value ? 'true' : 'false');
    });

    Array.prototype.forEach.call(document.querySelectorAll('[data-ccx-machine-option]'), function (input) {
      input.checked = input.value === value;
      var label = input.closest('.facet-checkbox');
      if (label) label.classList.toggle('active', input.checked);
    });

    var selected = document.querySelector('[data-ccx-machine-selected]');
    if (selected) selected.classList.toggle('hidden', !value);

    // Dawn's own count is server-rendered and does not know about this filter.
    var label = shown === 1 ? ' product' : ' products';
    ['ProductCountDesktop', 'ProductCount'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.textContent = shown + label;
    });

    var mobileCount = document.querySelector('[data-ccx-mobile-count]');
    if (mobileCount) mobileCount.textContent = shown + label;

    var sheetCount = document.querySelector('[data-ccx-sheet-count]');
    if (sheetCount) sheetCount.textContent = shown;
  }

  function setMachineFilter(value) {
    writeMachineParam(value);
    applyMachineFilter(value);
  }

  // The group is rendered inside #FacetFiltersForm so it sits first in the
  // sidebar, but it has no server-side facet behind it. facets.js submits on any
  // bubbling `input` event from that form, so this stops the event in the
  // capture phase to avoid a pointless re-render and the flash that comes with
  // it. The inputs carry no `name`, so they never reach the query string.
  document.addEventListener(
    'input',
    function (event) {
      if (event.target && event.target.closest && event.target.closest('[data-ccx-machine-option]')) {
        event.stopPropagation();
      }
    },
    true
  );

  /* ---------------------------------------------------------------------
   * Modal helpers (quick view, mobile sheet)
   * ------------------------------------------------------------------- */

  var openModal = null;
  var lastFocused = null;

  function showModal(modal, panel, focusTarget) {
    lastFocused = document.activeElement;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    openModal = modal;
    if (typeof trapFocus === 'function') {
      trapFocus(panel, focusTarget || panel.querySelector('button, [href], input'));
    } else if (focusTarget) {
      focusTarget.focus();
    }
  }

  function hideModal() {
    if (!openModal) return;
    openModal.hidden = true;
    document.body.style.overflow = '';
    openModal = null;
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(lastFocused);
    } else if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && openModal) hideModal();
  });

  /* ---------------------------------------------------------------------
   * Quick view (beans)
   * ------------------------------------------------------------------- */

  var quickViewProduct = null;

  function quickViewAddLabel(qty) {
    if (!quickViewProduct) return 'Add to Cart';
    var total = quickViewProduct.price * (parseInt(qty, 10) || 1);
    return 'Add to Cart · ' + formatMoney(total);
  }

  function openQuickView(card) {
    var modal = document.querySelector('[data-ccx-quick-view-modal]');
    if (!modal) return;

    quickViewProduct = {
      id: card.dataset.ccxProductId,
      variantId: card.dataset.ccxVariantId,
      title: card.dataset.ccxTitle || '',
      price: parseInt(card.dataset.ccxPrice, 10) || 0,
      image: card.dataset.ccxImage || '',
      roast: card.dataset.ccxRoast || '',
      feature: card.dataset.ccxFeature || '',
      handle: card.dataset.ccxHandle || ''
    };

    var image = modal.querySelector('[data-ccx-quick-view-image]');
    if (image) {
      image.src = quickViewProduct.image;
      image.alt = quickViewProduct.title;
    }

    var title = modal.querySelector('[data-ccx-quick-view-title]');
    if (title) title.textContent = quickViewProduct.title;

    var meta = modal.querySelector('[data-ccx-quick-view-meta]');
    if (meta) {
      var parts = [];
      if (quickViewProduct.roast) parts.push(quickViewProduct.roast + ' roast');
      if (quickViewProduct.feature) parts.push(quickViewProduct.feature);
      meta.textContent = parts.join(' · ');
    }

    var price = modal.querySelector('[data-ccx-quick-view-price]');
    if (price) price.textContent = card.dataset.ccxPriceFormatted || formatMoney(quickViewProduct.price);

    var qtyInput = modal.querySelector('[data-ccx-qty]');
    if (qtyInput) qtyInput.value = 1;

    var addButton = modal.querySelector('[data-ccx-quick-view-add]');
    if (addButton) {
      addButton.disabled = false;
      addButton.textContent = quickViewAddLabel(1);
      delete addButton.dataset.ccxLabelDefault;
    }

    var panel = modal.querySelector('[data-ccx-quick-view-panel]');
    showModal(modal, panel, modal.querySelector('[data-ccx-quick-view-close]'));

    capture('quick_view_opened', {
      product_id: quickViewProduct.id,
      product_handle: quickViewProduct.handle,
      product_title: quickViewProduct.title
    });
  }

  /* ---------------------------------------------------------------------
   * Mobile filter sheet
   * ------------------------------------------------------------------- */

  function activeFilterCount() {
    var form = facetForm();
    if (!form) return 0;
    return form.querySelectorAll('input[type="checkbox"]:checked').length;
  }

  function refreshMobileChrome() {
    var badge = document.querySelector('[data-ccx-filter-badge]');
    if (badge) {
      var count = activeFilterCount();
      badge.textContent = count > 0 ? ' (' + count + ')' : '';
    }

    var countSource = document.getElementById('ProductCountDesktop') || document.getElementById('ProductCount');
    if (!countSource) return;

    var digits = (countSource.textContent || '').replace(/[^0-9]/g, '');
    if (!digits) return;

    var mobileCount = document.querySelector('[data-ccx-mobile-count]');
    if (mobileCount) mobileCount.textContent = digits + ' products';

    var sheetCount = document.querySelector('[data-ccx-sheet-count]');
    if (sheetCount) sheetCount.textContent = digits;
  }

  function openSheet() {
    var sheet = document.querySelector('[data-ccx-sheet]');
    if (!sheet) return;
    var panel = sheet.querySelector('[data-ccx-sheet-panel]');
    var trigger = document.querySelector('[data-ccx-open-sheet]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    refreshMobileChrome();
    showModal(sheet, panel, sheet.querySelector('[data-ccx-close-sheet]'));
  }

  function closeSheet() {
    var trigger = document.querySelector('[data-ccx-open-sheet]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    hideModal();
  }

  /* ---------------------------------------------------------------------
   * Delegated events
   * ------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var target = event.target;

    // Quantity steppers
    var down = target.closest('[data-ccx-qty-down]');
    if (down) {
      event.preventDefault();
      stepQuantity(down.closest('.ccx-card__stepper'), -1);
      return;
    }

    var up = target.closest('[data-ccx-qty-up]');
    if (up) {
      event.preventDefault();
      stepQuantity(up.closest('.ccx-card__stepper'), 1);
      return;
    }

    // Card: Add to Cart
    var add = target.closest('[data-ccx-add]');
    if (add) {
      event.preventDefault();
      if (add.disabled) return;

      var card = add.closest('[data-ccx-card]');
      if (!card) return;

      var qtyField = card.querySelector('[data-ccx-qty]');
      var quantity = Math.max(1, parseInt(qtyField && qtyField.value, 10) || 1);

      add.disabled = true;
      addToCart(card.dataset.ccxVariantId, quantity)
        .then(function () {
          flashAdded(add);
          capture('collection_add_to_cart', {
            product_id: card.dataset.ccxProductId,
            product_handle: card.dataset.ccxHandle,
            product_title: card.dataset.ccxTitle,
            variant_id: card.dataset.ccxVariantId,
            quantity: quantity,
            line_value: (parseInt(card.dataset.ccxPrice, 10) || 0) * quantity,
            source: 'card'
          });
          return syncDeliveryBarFromCart();
        })
        .catch(function () {
          add.disabled = false;
        });
      return;
    }

    // Card: quick view
    var quickView = target.closest('[data-ccx-quick-view]');
    if (quickView) {
      event.preventDefault();
      var quickViewCard = quickView.closest('[data-ccx-card]');
      if (quickViewCard) openQuickView(quickViewCard);
      return;
    }

    // Quick view: Add to Cart
    var quickAdd = target.closest('[data-ccx-quick-view-add]');
    if (quickAdd) {
      event.preventDefault();
      if (quickAdd.disabled || !quickViewProduct) return;

      var modal = quickAdd.closest('[data-ccx-quick-view-modal]');
      var modalQty = modal.querySelector('[data-ccx-qty]');
      var modalQuantity = Math.max(1, parseInt(modalQty && modalQty.value, 10) || 1);
      var product = quickViewProduct;

      quickAdd.disabled = true;
      addToCart(product.variantId, modalQuantity)
        .then(function () {
          flashAdded(quickAdd, quickAdd.dataset.ccxLabelAdded);
          capture('collection_add_to_cart', {
            product_id: product.id,
            product_handle: product.handle,
            product_title: product.title,
            variant_id: product.variantId,
            quantity: modalQuantity,
            line_value: product.price * modalQuantity,
            source: 'quick_view'
          });
          return syncDeliveryBarFromCart();
        })
        .catch(function () {
          quickAdd.disabled = false;
        });
      return;
    }

    // Quick view: close
    if (target.closest('[data-ccx-quick-view-close]')) {
      event.preventDefault();
      hideModal();
      return;
    }

    var quickViewModal = document.querySelector('[data-ccx-quick-view-modal]');
    if (quickViewModal && target === quickViewModal) {
      hideModal();
      return;
    }

    // Pods: machine chips
    var chip = target.closest('[data-ccx-machine-chip]');
    if (chip) {
      event.preventDefault();
      var chipValue = chip.dataset.ccxMachineValue || '';

      capture('machine_chip_selected', { machine: chipValue || 'all' });

      if (isClientMachineMode()) {
        capture('filter_applied', {
          group: 'Machine compatibility',
          value: chipValue || 'all',
          source: 'chip',
          mode: 'client'
        });
        setMachineFilter(chipValue);
        return;
      }

      if (chipValue) {
        capture('filter_applied', { group: 'Machine compatibility', value: chipValue, source: 'chip' });
        selectSingleFacetValue(chipValue);
      } else {
        var sample = document.querySelector('[data-ccx-machine-chip][data-ccx-machine-value]:not([data-ccx-machine-value=""])');
        capture('filter_applied', { group: 'Machine compatibility', value: 'all', source: 'chip' });
        if (sample) clearFacetGroupOf(sample.dataset.ccxMachineValue);
      }

      var chips = document.querySelectorAll('[data-ccx-machine-chip]');
      Array.prototype.forEach.call(chips, function (button) {
        button.setAttribute('aria-pressed', button === chip ? 'true' : 'false');
      });
      return;
    }

    // Beans: in-stock toggle
    var stockToggle = target.closest('[data-ccx-stock-toggle]');
    if (stockToggle) {
      event.preventDefault();
      var nowOn = toggleFacetValue(stockToggle.dataset.ccxStockValue);
      stockToggle.setAttribute('aria-pressed', nowOn ? 'true' : 'false');
      capture('filter_applied', { group: 'Availability', value: 'In stock', active: nowOn, source: 'toggle' });
      return;
    }

    // Mobile sheet
    if (target.closest('[data-ccx-open-sheet]')) {
      event.preventDefault();
      openSheet();
      return;
    }

    if (target.closest('[data-ccx-close-sheet]')) {
      event.preventDefault();
      closeSheet();
      return;
    }

    var sheet = document.querySelector('[data-ccx-sheet]');
    if (sheet && target === sheet) {
      closeSheet();
      return;
    }

    var sheetChip = target.closest('[data-ccx-sheet-chip]');
    if (sheetChip) {
      event.preventDefault();
      if (sheetChip.disabled) return;
      var on = toggleFacetValue(sheetChip.dataset.ccxSheetValue);
      sheetChip.setAttribute('aria-pressed', on ? 'true' : 'false');
      capture('filter_applied', {
        group: sheetChip.dataset.ccxSheetGroup,
        value: sheetChip.dataset.ccxSheetValue,
        active: on,
        source: 'mobile_sheet'
      });
      window.setTimeout(refreshMobileChrome, 400);
      return;
    }

    var sortChip = target.closest('[data-ccx-sheet-sort]');
    if (sortChip) {
      event.preventDefault();
      var select = document.getElementById('SortBy');
      if (select) {
        select.value = sortChip.dataset.ccxSortValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      var sortChips = document.querySelectorAll('[data-ccx-sheet-sort]');
      Array.prototype.forEach.call(sortChips, function (button) {
        button.setAttribute('aria-pressed', button === sortChip ? 'true' : 'false');
      });
      window.setTimeout(refreshMobileChrome, 400);
      return;
    }

    // View basket
    if (target.closest('[data-ccx-view-basket]')) {
      capture('view_basket_clicked', { source: 'delivery_bar' });
      return;
    }

    // Sold out: notify me
    var notify = target.closest('[data-ccx-notify]');
    if (notify) {
      var notifyCard = notify.closest('[data-ccx-card]');
      capture('notify_me_clicked', {
        product_id: notifyCard ? notifyCard.dataset.ccxProductId : '',
        product_handle: notifyCard ? notifyCard.dataset.ccxHandle : ''
      });
    }
  });

  document.addEventListener('change', function (event) {
    // Sidebar filters. Captured on `change` rather than on the label click, so
    // the reported state is the checkbox's final state, not a guess about
    // whether label activation has run yet. Programmatic toggles from the chips
    // dispatch `input` only, so they cannot double-report here.
    var machineInput = event.target.closest('[data-ccx-machine-option]');
    if (machineInput) {
      var next = machineInput.checked ? machineInput.value : '';
      capture('filter_applied', {
        group: 'Machine compatibility',
        value: next || 'all',
        active: !!next,
        source: 'sidebar',
        mode: 'client'
      });
      setMachineFilter(next);
      return;
    }

    var facetInput = event.target.closest('#FacetFiltersForm input[type="checkbox"]');
    if (facetInput) {
      var facetGroup = facetInput.closest('.js-filter');
      var groupLabel = facetGroup ? facetGroup.querySelector('.facets__summary-label') : null;
      capture('filter_applied', {
        group: groupLabel ? groupLabel.textContent.trim().replace(/\s*\(\d+\)$/, '') : '',
        value: facetInput.value,
        active: facetInput.checked,
        source: 'sidebar'
      });
      window.setTimeout(refreshMobileChrome, 1200);
      return;
    }

    // Keep the quick view's "Add to Cart · £total" in step with the stepper.
    var input = event.target.closest('[data-ccx-qty]');
    if (!input) return;

    var value = Math.max(1, parseInt(input.value, 10) || 1);
    input.value = value;

    var modal = input.closest('[data-ccx-quick-view-modal]');
    if (!modal || !quickViewProduct) return;

    var addButton = modal.querySelector('[data-ccx-quick-view-add]');
    if (addButton && !addButton.disabled) addButton.textContent = quickViewAddLabel(value);
  });

  /* ---------------------------------------------------------------------
   * Cart state
   * ------------------------------------------------------------------- */

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.cartData && typeof event.cartData.item_count === 'number') {
        renderDeliveryBar(event.cartData);
      } else {
        syncDeliveryBarFromCart();
      }
    });
  }

  // Catch cart mutations from anywhere the pub/sub does not reach: the cart
  // notification, quick order lists, and third-party apps.
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function () {
      var url = (arguments[0] && arguments[0].url) || arguments[0] || '';
      var promise = nativeFetch.apply(this, arguments);
      if (typeof url === 'string' && CART_MUTATION.test(url)) {
        promise
          .then(function () {
            syncDeliveryBarFromCart();
          })
          .catch(function () {});
      }
      return promise;
    };
  }

  if (window.XMLHttpRequest) {
    var nativeOpen = XMLHttpRequest.prototype.open;
    var nativeSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._ccxUrl = url;
      return nativeOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (typeof this._ccxUrl === 'string' && CART_MUTATION.test(this._ccxUrl)) {
        this.addEventListener('load', function () {
          syncDeliveryBarFromCart();
        });
      }
      return nativeSend.apply(this, arguments);
    };
  }

  document.addEventListener('shopify:section:load', function () {
    readMoneyFormat();
    syncDeliveryBarFromCart();
    refreshMobileChrome();
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) syncDeliveryBarFromCart();
  });

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) syncDeliveryBarFromCart();
  });

  /* ---------------------------------------------------------------------
   * Boot
   * ------------------------------------------------------------------- */

  function init() {
    readMoneyFormat();

    var bar = document.querySelector('[data-ccx-delivery-bar]');
    if (bar) wasUnlocked = bar.dataset.unlocked === 'true';

    refreshMobileChrome();
    applyMachineFilter(currentMachine());
    capture('collection_viewed', {});

    // facets.js swaps out #ProductGridContainer and the facet panels; refresh
    // the mobile chrome whenever it does.
    var container = document.getElementById('ProductGridContainer');
    if (container && window.MutationObserver) {
      new MutationObserver(function () {
        applyMachineFilter(currentMachine());
        refreshMobileChrome();
      }).observe(container, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();